import ConditionalTooltip from '@components/ConditionalTooltip';
import OverlayTriggerWithInjectedIntl from '@components/OverlayTriggerWithInjectedIntl';
import { CalculationParams } from '@features/settings/reducer';
import NumberInput from '@rio-cloud/rio-uikit/NumberInput';
import Switch from '@rio-cloud/rio-uikit/Switch';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import cn from 'classnames';
import { CSSProperties, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { CALCULATION_PARAMS, shouldHideCalculationParam } from './constants';

interface FuelCalculationParametersProps {
    calculationParams: CalculationParams;
    handleCalcParamSwitchChange: (calcParamName: keyof CalculationParams, enabled: boolean) => void;
    handleCalcParamWeightChange: (calcParamName: keyof CalculationParams, newWeight?: number) => void;
    shouldShowHarshKPIs: boolean;
    shouldShowExcessiveKPIS: boolean;
    shouldShowElectricRatings: boolean;
}

const AvailablePercentageTooltipContent = () => (
    <FormattedMessage
        id="administration.rating.parameterAdaptation.available.tooltip"
        values={{
            b: (chunks: unknown[]) => <strong>{chunks as React.ReactNode}</strong>,
        }}
    />
);

const isExcludedFuelCalculationParam = (calcParamName: string) => {
    return calcParamName.includes('electric');
};

const FuelCalculationParameters = ({
    calculationParams,
    handleCalcParamSwitchChange,
    handleCalcParamWeightChange,
    shouldShowHarshKPIs,
    shouldShowExcessiveKPIS,
    shouldShowElectricRatings,
}: FuelCalculationParametersProps) => {
    const availablePercentage = useMemo(() => {
        const total = Object.keys(calculationParams).reduce((acc, calcParamName) => {
            if (calcParamName === 'recuperationRating' || calcParamName.includes('electric')) return acc;
            if (shouldHideCalculationParam(calcParamName, shouldShowHarshKPIs, shouldShowExcessiveKPIS)) return acc;
            const param = calculationParams[calcParamName as keyof CalculationParams];
            return acc + (typeof param === 'object' && 'weight' in param ? param.weight! : 0);
        }, 0);
        return 100 - total;
    }, [calculationParams, shouldShowHarshKPIs, shouldShowExcessiveKPIS]);

    return (
        <div className="display-flex flex-column">
            <div className="display-flex flex-column gap-5 margin-top-15">
                <div className="display-flex align-items-center justify-content-center padding-y-2">
                    <span className="rioglyph rioglyph-filling-station text-size-h3 margin-right-10"></span>
                    <span>
                        <FormattedMessage id="vehicleFuelType.fuel" />
                    </span>
                </div>
                {Object.keys(CALCULATION_PARAMS).map(key => {
                    const calcParamName = key as keyof typeof CALCULATION_PARAMS;
                    const { translation } = CALCULATION_PARAMS[calcParamName];
                    const param = calculationParams[calcParamName];
                    const { weight, enabled } = param;

                    if (
                        shouldHideCalculationParam(key, shouldShowHarshKPIs, shouldShowExcessiveKPIS) ||
                        isExcludedFuelCalculationParam(calcParamName)
                    ) {
                        return null;
                    }

                    if (key === CALCULATION_PARAMS.recuperationRating.translation && !shouldShowElectricRatings) {
                        return null;
                    }

                    return (
                        <div
                            key={key}
                            className="display-flex bg-white padding-y-3 padding-x-5 border width-200"
                            style={{ height: '40px' }}
                        >
                            {key === CALCULATION_PARAMS.recuperationRating.translation ? (
                                <div className="display-flex align-items-center justify-content-center width-100pct">
                                    <span className="text-medium text-color-dark">
                                        <FormattedMessage id="unavailable" />
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <div className="display-flex align-items-center justify-content-end margin-left-5 margin-right-10 width-100">
                                        <span
                                            className="margin-right-10 text-medium"
                                            data-test={`${translation}Switch`}
                                            style={{ userSelect: 'none' } as CSSProperties}
                                        >
                                            {enabled ? (
                                                <FormattedMessage id="setting.activated" />
                                            ) : (
                                                <FormattedMessage id="setting.deactivated" />
                                            )}
                                        </span>
                                        <Switch
                                            keyName={`${translation}Switch`}
                                            onChange={(enabled: boolean) =>
                                                handleCalcParamSwitchChange(calcParamName, enabled)
                                            }
                                            checked={enabled}
                                        />
                                    </div>
                                    <div
                                        className="display-flex align-items-center margin-right-5"
                                        data-test={`${translation}Input`}
                                    >
                                        <div className="margin-0 width-50">
                                            <NumberInput
                                                className="text-right margin-right-5"
                                                style={{
                                                    paddingTop: '1px',
                                                    paddingBottom: '1px',
                                                    width: '48px',
                                                    height: '28px',
                                                }}
                                                min={0}
                                                max={100}
                                                value={weight!}
                                                disabled={!enabled}
                                                onChange={(newWeight?: number) =>
                                                    handleCalcParamWeightChange(calcParamName, newWeight)
                                                }
                                            />
                                        </div>
                                        <span className="text-color-dark margin-left-5">%</span>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            <div
                className="display-flex justify-content-end align-items-center"
                data-test="fuelParameterAdaptionAvailable"
            >
                {availablePercentage !== 0 && (
                    <OverlayTriggerWithInjectedIntl
                        placement="left"
                        show={true}
                        overlay={
                            <Tooltip id="fuel-available-percentage-sticky-tooltip" width="auto">
                                <AvailablePercentageTooltipContent />
                            </Tooltip>
                        }
                    >
                        <span className="margin-right-0"></span>
                    </OverlayTriggerWithInjectedIntl>
                )}
                <ConditionalTooltip
                    enabled={true}
                    getTooltipChildren={() => <AvailablePercentageTooltipContent />}
                    placement="left"
                    width="auto"
                >
                    <span
                        className="padding-left-10 margin-right-10 text-color-dark line-height"
                        style={{ lineHeight: '30px' }}
                    >
                        <FormattedMessage id="administration.rating.parameterAdaptation.available" />:{' '}
                        <span className={cn('text-color-' + (availablePercentage < 0 ? 'danger' : 'dark'))}>
                            {availablePercentage}%
                        </span>
                    </span>
                </ConditionalTooltip>
            </div>
        </div>
    );
};

export default FuelCalculationParameters;
